<template>
  <VBottomSheet
    :value="value"
    content-class="base-bottom-sheet"
    overlay-color="rgba(31, 41, 55, 0.4)"
    overlay-opacity="1"
    @input="$emit('input', $event)"
  >
    <div
      ref="surface"
      class="base-bottom-sheet__surface"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="title ? titleId : null"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div class="base-bottom-sheet__head">
        <span class="base-bottom-sheet__grabber" aria-hidden="true"></span>
        <!-- On a second level, ‹ goes up one, as back does. -->
        <div v-if="canGoBack" class="base-bottom-sheet__title-row">
          <button type="button" class="base-bottom-sheet__back" :aria-label="$t('actions.back')" @click="$emit('back')">
            <BaseIcon name="chevron-left" aria-hidden="true" />
          </button>
          <p v-if="title" :id="titleId" class="base-bottom-sheet__title">{{ title }}</p>
        </div>
        <p v-else-if="title" :id="titleId" class="base-bottom-sheet__title">{{ title }}</p>
      </div>
      <slot />
    </div>
  </VBottomSheet>
</template>

<script>
import { VBottomSheet } from 'vuetify/lib';
import BaseIcon from '@/components/base/BaseIcon';
import { closeOnBack } from '@/util/back-button';

// Swiped down past this many pixels, the sheet closes; otherwise it snaps back. As the filter
// chips' sheet.
const DISMISS_THRESHOLD = 90;

// A sheet pinned to the bottom of the screen, for phone menus. Looks and swipes like the filter
// chips' sheet; Vuetify's dialog underneath brings the focus trap, Esc and the dimmed backdrop.
export default {
  name: 'BaseBottomSheet',
  components: { BaseIcon, VBottomSheet },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    // A second level (a row's labels, after its actions): ‹ before the title, and back, emit
    // `back` for the page to show the first level again.
    canGoBack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      titleId: `base-bottom-sheet-title-${this._uid}`,
      offset: 0,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        this.releaseBackButton();
        if (!isOpen) return;
        this.releaseBack = closeOnBack(() => this.$emit('input', false));
        if (this.canGoBack) this.holdLevel();
      },
    },
    canGoBack(isLevel) {
      this.releaseLevel();
      if (isLevel && this.value) this.holdLevel();
    },
  },
  beforeDestroy() {
    this.releaseBackButton();
  },
  methods: {
    releaseBackButton() {
      if (this.releaseBack) this.releaseBack();
      this.releaseBack = null;
      this.releaseLevel();
    },
    // Back on a second level goes up one, an overlay of its own over the sheet's.
    holdLevel() {
      this.releaseLevelBack = closeOnBack(() => this.$emit('back'));
    },
    releaseLevel() {
      if (this.releaseLevelBack) this.releaseLevelBack();
      this.releaseLevelBack = null;
    },
    // The dialog box around the content: it scrolls, and it's what the finger moves.
    sheet() {
      return this.$refs.surface.parentElement;
    },
    // Swipe down to close, from the head, or from the content once it's scrolled to the top
    // (a swipe anywhere else scrolls the content).
    onTouchStart(event) {
      const fromHead = event.target.closest && event.target.closest('.base-bottom-sheet__head');
      this.dragStart = fromHead || this.sheet().scrollTop <= 0 ? event.touches[0].clientY : null;
    },
    onTouchMove(event) {
      if (this.dragStart == null) return;
      const delta = event.touches[0].clientY - this.dragStart;
      // Upwards: the content's own scrolling.
      if (delta <= 0) {
        this.moveTo(0);
        return;
      }
      if (event.cancelable) event.preventDefault();
      this.moveTo(delta);
    },
    onTouchEnd() {
      if (this.dragStart == null) return;
      const dismiss = this.offset > DISMISS_THRESHOLD;
      this.dragStart = null;
      this.moveTo(0);
      if (dismiss) this.$emit('input', false);
    },
    // Follows the finger 1:1; back at 0, the dialog's own transition snaps it into place.
    moveTo(offset) {
      this.offset = offset;
      const { style } = this.sheet();
      style.transform = offset ? `translateY(${offset}px)` : '';
      style.transition = offset ? 'none' : '';
    },
  },
};
</script>

<style lang="scss">
.base-bottom-sheet.v-dialog {
  background: $color-white;
  border-radius: $radius-md $radius-md 0 0;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
  max-height: 85%;
  overflow-y: auto;
}

.base-bottom-sheet {
  &__surface {
    display: flex;
    flex-direction: column;
    padding: 8px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  }

  // The grabber and the title: where a swipe always moves the sheet.
  &__head {
    display: flex;
    flex-direction: column;
  }

  &__grabber {
    align-self: center;
    width: 36px;
    height: 4px;
    margin: 6px 0 10px;
    border-radius: $radius-full;
    background: $color-slate-300;
  }

  // Its 40px reach past the line, which stays the title's height.
  &__title-row {
    display: flex;
    align-items: center;
    margin: 0 0 8px;

    .base-bottom-sheet__title {
      margin: 0;
    }
  }

  &__back {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: -11px 0 -11px -10px;
    color: $color-text-body;

    .base-icon {
      width: 20px;
      height: 20px;
    }
  }

  &__title {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: bold;
    color: $color-text-body;
  }

  // What a row of a list holds beyond what it shows, label and value per line, above the
  // sheet's actions. A `div` may group a pair.
  &__facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px 12px;
    margin: -4px 0 8px;
    font-size: 13px;
    color: $color-slate-500;

    > div {
      display: contents;
    }

    dd {
      min-width: 0;
      margin: 0;
      color: $color-text-body;
    }
  }

  // A list of choices, one per line.
  &__menu {
    margin: 0;
    padding: 0;
    list-style: none;

    // A link (call, write, open a page) looks like the other choices.
    button,
    a {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      min-height: 48px;
      padding: 0 4px;
      font-size: 15px;
      color: $color-text-body;
      text-align: left;
      text-decoration: none;

      .base-icon {
        flex: none;
        width: 20px;
        height: 20px;
      }

      &.is-active {
        color: $color-accent;
        font-weight: 500;
      }

      &.is-danger {
        color: $color-error;
      }

      &:disabled {
        opacity: 0.5;
      }
    }
  }

  // Under an unavailable choice, why: not greyed with it, in line with its label (after the
  // 4px padding, the 20px icon and the 12px gap).
  &__menu-note {
    margin: -8px 0 8px 36px;
    font-size: 12px;
    color: $color-slate-500;
  }

  // A caption over the choices that follow it, grouping them.
  &__menu-heading {
    padding: 12px 4px 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $color-slate-500;
  }

  // A status's colour, before its name.
  &__swatch {
    flex: none;
    width: 14px;
    height: 14px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: $radius-full;
  }

  // At the end of a line: the check on the current choice, or an arrow saying the tap leads
  // further (more choices, another tab) instead of acting at once.
  &__menu-check,
  &__menu-end {
    margin-left: auto;
  }

  &__menu-end {
    color: $color-slate-500;
  }
}
</style>
